import { Fragment } from 'react';
import { useIntl } from 'react-intl';
import { useEventGeneratorClickCta } from 'src/analytics/useEventGeneratorClickCta';
import { Button } from 'src/components';
import { useTargetMessages } from 'src/hooks/useTargetMessages';
import { getExternalLinkFormatters } from 'src/utils/externalLinkFormatters';
import { useCtaHref } from 'src/hooks/useCtaHref';
import { CTAContentBlock } from '../CTAContentBlock';
import { MoviesFAQ } from '../MoviesFAQ';
import { MoviesJumbotron } from '../MoviesJumbotron';
import { SideNoteBlock } from '../SideNoteBlock';
import { TagPageProps } from '../types/tagPageProps';
import { Movie } from '../types/movie';
import { useExtraContentCTAs } from './useExtraContentCTAs';
import * as S from './styles';
import * as msgsData from './messages';

const { msgs, ...localesWithMessages } = msgsData;

type MoviesTagPageContentProps = Pick<
  TagPageProps,
  'immersiveScenarios' | 'membotMissions' | 'scenarios' | 'tag'
> & {
  movies: Movie[];
};

const MOVIES_FAQ_ID = 'tips-for-learning';
const WHY_MEMRISE_ID = 'why-learn-with-memrise';

const externalLinkFormatters = getExternalLinkFormatters(S.Link);

export const MoviesTagPageContent = ({
  immersiveScenarios,
  membotMissions,
  scenarios,
  tag,
  movies,
}: MoviesTagPageContentProps) => {
  const intl = useIntl();

  const targetMsgs = useTargetMessages(localesWithMessages);

  const signUpHref = useCtaHref();
  const eventGeneratorClickCta = useEventGeneratorClickCta();

  const { membotCta, membotCtaIndex, interestingContentCta, interestingContentCtaIndex } =
    useExtraContentCTAs(movies);

  return (
    <div data-testid="moviesTagPageContent">
      <MoviesJumbotron tag={tag} />

      <S.GridContainer>
        <S.Grid>
          <S.GridTop>
            {intl.formatMessage(targetMsgs.moviesDescription, {
              ...externalLinkFormatters,
              lineBreak: <br />,
            })}
          </S.GridTop>

          <S.GridBottom>
            {movies.map((movie, index) => (
              <Fragment key={movie.slug}>
                <S.MovieCard movie={movie} number={index + 1} />
                {movie.immerseCta ||
                index === membotCtaIndex ||
                index === interestingContentCtaIndex ? (
                  <S.CTAContentBlockContainer>
                    {movie.immerseCta && <CTAContentBlock contentCta={movie.immerseCta} />}

                    {index === membotCtaIndex && <CTAContentBlock contentCta={membotCta} />}

                    {index === interestingContentCtaIndex && (
                      <CTAContentBlock contentCta={interestingContentCta} />
                    )}
                  </S.CTAContentBlockContainer>
                ) : (
                  index + 1 !== movies.length && <S.Divider />
                )}
              </Fragment>
            ))}

            <S.MoviesFAQContainer>
              <S.AnchorContainer id={MOVIES_FAQ_ID}>
                <MoviesFAQ />
              </S.AnchorContainer>
            </S.MoviesFAQContainer>

            <S.AnchorContainer id={WHY_MEMRISE_ID}>
              <S.MemriseExperienceSecondBackground />
              <SideNoteBlock
                backgroundColor="secondary20"
                invertTextColor
                title={intl.formatMessage(targetMsgs.memriseExperienceTitle)}
                description={intl.formatMessage(targetMsgs.memriseExperienceDescription)}
              />

              {immersiveScenarios.length > 0 && (
                <S.LICBlock
                  description={intl.formatMessage(targetMsgs.immerseDescription)}
                  immersiveScenarios={immersiveScenarios}
                  title={intl.formatMessage(targetMsgs.immerseTitle)}
                  type="immerse"
                />
              )}

              {membotMissions.length > 0 && (
                <S.LICBlock
                  description={intl.formatMessage(targetMsgs.communicateDescription)}
                  membotMissions={membotMissions}
                  title={intl.formatMessage(targetMsgs.communicateTitle)}
                  type="communicate"
                />
              )}

              {scenarios.length > 0 && (
                <S.LICBlock
                  description={intl.formatMessage(targetMsgs.learnDescription)}
                  scenarios={scenarios}
                  title={intl.formatMessage(targetMsgs.learnTitle)}
                  type="learn"
                />
              )}

              <S.SideNoteBlockContainer>
                <SideNoteBlock
                  backgroundColor="secondary99"
                  title={intl.formatMessage(msgs.memriseNotOnlyFlashcardsTitle)}
                  description={intl.formatMessage(targetMsgs.memriseNotOnlyFlashcardsDescription, {
                    lineBreak: <br />,
                  })}
                >
                  <S.SideNoteBlockButtonContainer>
                    <Button
                      analyticsEvent={eventGeneratorClickCta}
                      buttonSize="large"
                      buttonType="primary"
                      fullWidth="md"
                      href={signUpHref}
                    >
                      {intl.formatMessage(msgs.registerToLearn)}
                    </Button>
                  </S.SideNoteBlockButtonContainer>
                </SideNoteBlock>
              </S.SideNoteBlockContainer>
            </S.AnchorContainer>
          </S.GridBottom>

          <S.TableOfContents
            orderedLinks={movies.map(({ slug, title }) => ({
              id: slug,
              text: title,
            }))}
            unorderedLinks={[
              {
                id: MOVIES_FAQ_ID,
                text: intl.formatMessage(targetMsgs.moviesFAQ),
              },
              {
                id: WHY_MEMRISE_ID,
                text: intl.formatMessage(targetMsgs.whyMemrise),
              },
            ]}
          />
        </S.Grid>
      </S.GridContainer>
    </div>
  );
};
